import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

export interface UseScrollFollowResult {
  scrollRef: RefObject<HTMLDivElement>;
  sentinelRef: RefObject<HTMLDivElement>;
  autoFollow: boolean;
  setAutoFollow: (v: boolean) => void;
  handleScroll: () => void;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
}

/**
 * Manages scroll-follow behavior for a scrollable container.
 */
export function useScrollFollow(thresholdPx: number = 64): UseScrollFollowResult {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [autoFollow, setAutoFollow] = useState(true);
  // Ref mirrors autoFollow synchronously so observers never read stale state.
  const autoFollowRef = useRef(autoFollow);
  autoFollowRef.current = autoFollow;

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const shouldFollow = distance <= thresholdPx;
    autoFollowRef.current = shouldFollow;
    setAutoFollow(shouldFollow);
  }, [thresholdPx]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  // Observe when the bottom sentinel is visible inside the scroll container.
  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target || typeof IntersectionObserver === "undefined") return;
    let stopped = false;
    const io = new IntersectionObserver(
      entries => {
        if (stopped) return;
        const entry = entries[0];
        // If sentinel is in view, we are at/near bottom → enable follow.
        const isVisible = entry?.isIntersecting === true;
        autoFollowRef.current = isVisible;
        setAutoFollow(isVisible);
      },
      { root, threshold: 1.0, rootMargin: "0px 0px 1px 0px" }
    );
    io.observe(target);
    return () => {
      stopped = true;
      try {
        io.disconnect();
      } catch {}
    };
  }, []);

  // Keep pinned to bottom when the container resizes (e.g., rotation, viewport changes).
  // Uses ref instead of state to avoid stale closures during rapid streaming.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let stopped = false;
    const ro = new ResizeObserver(() => {
      if (stopped) return;
      if (autoFollowRef.current) {
        // Jump immediately to bottom to avoid visual jank on mobile.
        el.scrollTop = el.scrollHeight;
      }
    });
    ro.observe(el);
    return () => {
      stopped = true;
      try {
        ro.disconnect();
      } catch {}
    };
  }, []);

  // On mount, ensure initial position is bottom if autoFollow is true.
  useLayoutEffect(() => {
    if (autoFollow) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { scrollRef, sentinelRef, autoFollow, setAutoFollow, handleScroll, scrollToBottom };
}
